import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowPathIcon } from '@heroicons/react/24/solid';
import type { ScriptShot, ScriptScene, ScriptCharacter, ScriptProp } from '@packages/global';
import ShotCard from '../cards/ShotCard';

interface ShotsSectionProps {
  shots: ScriptShot[];
  scriptId?: string;
  onUpdate?: () => void;
  scenes?: ScriptScene[];
  characters?: ScriptCharacter[];
  props?: ScriptProp[];
  /** Whether shot generation is in progress */
  isGenerating?: boolean;
  /** Shot generation progress */
  generatingProgress?: { current: number; total: number; isGenerating: boolean };
  /** Indices of newly arrived shots (for animation) */
  newShotIndices?: number[];
  /** Callback to clear new shot indices after animation */
  onClearNewShotIndices?: () => void;
}

export default function ShotsSection({
  shots,
  scriptId,
  onUpdate,
  scenes = [],
  characters = [],
  props = [],
  isGenerating = false,
  generatingProgress,
  newShotIndices = [],
  onClearNewShotIndices
}: ShotsSectionProps) {
  const { t } = useTranslation(['script', 'common']);
  // Track which shots have completed their entrance animation
  const [animatedShots, setAnimatedShots] = useState<Set<number>>(new Set());

  // Handle animation completion for new shots
  useEffect(() => {
    if (newShotIndices.length > 0) {
      // Trigger animation by adding to animated set after a brief delay
      const timer = setTimeout(() => {
        setAnimatedShots(prev => {
          const next = new Set(prev);
          newShotIndices.forEach(idx => next.add(idx));
          return next;
        });
        // Clear the new shot indices after animation starts
        onClearNewShotIndices?.();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [newShotIndices, onClearNewShotIndices]);

  if (!shots || shots.length === 0) return null;

  // Helper function to determine if a shot should show entrance animation
  const isNewShot = (index: number) => newShotIndices.includes(index) && !animatedShots.has(index);

  return (
    <div>
      <h3 className="text-sm font-medium text-gray-300 mb-2">{t('step.shots')}</h3>

      {/* Progress indicator when generating */}
      {generatingProgress?.isGenerating && (
        <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-blue-600/20 border border-blue-500/30 rounded-lg">
          <ArrowPathIcon className="w-4 h-4 text-blue-400 animate-spin" />
          <span className="text-sm text-blue-300">
            {t('shots.generatingProgress', { current: generatingProgress.current, total: generatingProgress.total })}
          </span>
        </div>
      )}

      {/* Grid layout for shot cards */}
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(480px, 1fr))' }}>
        {shots.map((shot, idx) => (
          <ShotCard
            key={idx}
            shot={shot}
            index={idx}
            scriptId={scriptId}
            scenes={scenes}
            characters={characters}
            props={props}
            onUpdate={onUpdate}
            isNew={isNewShot(idx)}
            allShots={shots}
          />
        ))}
      </div>
    </div>
  );
}
